/**
 * Image Studio Recipe — standalone image generation page.
 *
 * Lives at /recipes/image-studio (registered in App.js, slug-matched via
 * the recipe's `custom_route` seed field).
 *
 * Phase 1 capabilities surfaced here:
 *   • Single prompt OR multi-prompt textarea
 *   • Paste-a-breakdown box (auto-splits on [SHxx] markers backend-side)
 *   • Engine select (gpt-image-2 / gemini / auto)  ← gemini Pass 2
 *   • Aspect ratio + resolution (1k / 2k) + count (1-4)
 *   • Reference uploader (character / costume / style / subject)
 *   • Estimate button (pre-flight cost), then Generate
 *   • Results gallery — click to expand · download · regen with same prompt
 *
 * Pricing transparency: every cost shown is the RAW OpenAI rate via our
 * BYO keys; no platform markup. The estimate matches the actual within
 * the precision of the API's per-image price (no audit add-on in v1).
 */
import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Sparkles, Loader2, Upload, X, Download, RefreshCw, Image as ImageIcon,
  ChevronLeft, DollarSign, Settings2, Maximize2, Paperclip,
} from "lucide-react";

const API = window.__API_BASE + "/api";
const BASE_URL = window.__API_BASE;

const ROLES = ["character", "costume", "style", "subject"];
const ASPECTS = ["16:9", "9:16", "1:1", "4:5", "3:2"];

export default function ImageStudioRecipe() {
  const navigate = useNavigate();

  // ─── Input state ────────────────────────────────────────────────
  const [mode, setMode] = useState("single"); // 'single' | 'batch'
  const [prompt, setPrompt] = useState("");
  const [breakdownText, setBreakdownText] = useState("");
  const [engine, setEngine] = useState("gpt-image-2");
  const [aspectRatio, setAspectRatio] = useState("16:9");
  const [resolution, setResolution] = useState("2k");
  const [count, setCount] = useState(1);
  const [negativePrompt, setNegativePrompt] = useState("");
  const [seed, setSeed] = useState("");
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // ─── References state ──────────────────────────────────────────
  const [refs, setRefs] = useState([]);            // all of user's refs from server
  const [selectedRefIds, setSelectedRefIds] = useState(new Set());
  const [uploadingRef, setUploadingRef] = useState(false);
  const refFileInputRef = useRef(null);
  const [refRoleOnUpload, setRefRoleOnUpload] = useState("character");

  // ─── Run state ─────────────────────────────────────────────────
  const [estimate, setEstimate] = useState(null);
  const [estimating, setEstimating] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [currentRun, setCurrentRun] = useState(null);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [runHistory, setRunHistory] = useState([]);

  // ─── Load refs + history on mount ──────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/recipes/image/refs`);
        setRefs(r.data.refs || []);
      } catch (e) { /* ignore — empty list is fine */ }
      try {
        const r = await axios.get(`${API}/recipes/image/runs?limit=12`);
        setRunHistory(r.data.runs || []);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  // ─── Helpers ───────────────────────────────────────────────────
  const promptCount = useMemo(() => {
    if (mode === "batch" && breakdownText.trim()) {
      const matches = breakdownText.match(/^[\s>•\-]*(?:\[\s*)?(?:SH|SHOT)\s*[-_]?\s*\d{1,3}/gim);
      return Math.max(1, matches?.length || 1);
    }
    return prompt.trim() ? 1 : 0;
  }, [mode, prompt, breakdownText]);

  const buildPayload = () => {
    const payload = {
      engine, aspect_ratio: aspectRatio, resolution,
      count: Math.max(1, Math.min(4, count)),
      reference_image_ids: Array.from(selectedRefIds),
    };
    if (negativePrompt.trim()) payload.negative_prompt = negativePrompt.trim();
    if (seed && !isNaN(parseInt(seed, 10))) payload.seed = parseInt(seed, 10);
    if (mode === "batch" && breakdownText.trim()) {
      payload.breakdown_text = breakdownText.trim();
    } else {
      payload.prompt = prompt.trim();
    }
    return payload;
  };

  const fetchEstimate = async () => {
    if (promptCount === 0) { toast.error("Type a prompt first"); return; }
    setEstimating(true);
    try {
      const { data } = await axios.post(`${API}/recipes/image/estimate`, buildPayload());
      setEstimate(data);
    } catch (e) {
      toast.error("Estimate failed: " + (e.response?.data?.detail || e.message));
    }
    setEstimating(false);
  };

  const runGeneration = async () => {
    if (promptCount === 0) { toast.error("Type a prompt first"); return; }
    setGenerating(true);
    setCurrentRun(null);
    try {
      const { data } = await axios.post(`${API}/recipes/image/generate`, buildPayload(), {
        timeout: 30000, // /generate now returns the run_id immediately
      });
      // 2026-06-27 — /generate is always async now (dodges Cloudflare edge
      // 502s on long gpt-image-2 HIGH calls). Every response carries a
      // run_id; we poll until done. If the server ever flips back to
      // returning a finished result inline, the early-return below picks
      // that up too.
      if (data.run_id && data.status !== "done" && data.status !== "partial" && data.status !== "failed") {
        toast.info(`Run ${data.run_id.slice(0, 8)}… queued (${data.n_prompts} prompts · est $${data.estimated_total_usd ?? "?"})`);
        await pollRun(data.run_id);
      } else {
        setCurrentRun(data);
        const ok = (data.results || []).filter(r => (r.image_urls || []).length).length;
        const failed = (data.results || []).filter(r => r.error).length;
        if (failed === 0) {
          toast.success(`Done — ${ok} image${ok === 1 ? "" : "s"} · $${data.total_cost_usd}`);
        } else {
          toast.warning(`Done — ${ok} ok, ${failed} failed · $${data.total_cost_usd}`);
        }
        try {
          const r = await axios.get(`${API}/recipes/image/runs?limit=12`);
          setRunHistory(r.data.runs || []);
        } catch (e) { /* ignore */ }
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error("Generation failed: " + (typeof detail === "string" ? detail : (detail?.message || e.message)));
    }
    setGenerating(false);
  };

  const pollRun = async (runId) => {
    const start = Date.now();
    while (Date.now() - start < 900000) { // 15 min cap
      await new Promise(r => setTimeout(r, 3000));
      try {
        const { data } = await axios.get(`${API}/recipes/image/runs/${runId}`);
        if (data.status === "done" || data.status === "partial" || data.status === "failed") {
          setCurrentRun(data);
          if (data.status === "failed") {
            toast.error("Run failed: " + (data.error || "see logs"));
          } else {
            const ok = (data.results || []).filter(r => (r.image_urls || []).length).length;
            const failed = (data.results || []).filter(r => r.error).length;
            if (failed === 0) {
              toast.success(`Done — ${ok} image${ok === 1 ? "" : "s"} · $${data.total_cost_usd}`);
            } else {
              toast.warning(`Done — ${ok} ok, ${failed} failed · $${data.total_cost_usd}`);
            }
          }
          try {
            const h = await axios.get(`${API}/recipes/image/runs?limit=12`);
            setRunHistory(h.data.runs || []);
          } catch (e) { /* ignore */ }
          return;
        }
        // While running, surface partial progress in the toast/state.
        if (data.status === "running") {
          setCurrentRun(data);
        }
      } catch { /* keep polling */ }
    }
    toast.warning("Poll timed out — open the run in history.");
  };

  const handleRefFiles = async (fileList, role) => {
    if (!fileList || fileList.length === 0) return;
    setUploadingRef(true);
    let added = 0;
    for (const f of Array.from(fileList)) {
      if (!f.type.startsWith("image/")) continue;
      try {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("role", role || refRoleOnUpload);
        if (f.name) fd.append("label", f.name);
        const { data } = await axios.post(`${API}/recipes/image/refs`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setRefs(prev => [data, ...prev]);
        setSelectedRefIds(prev => new Set([...prev, data.ref_id]));
        added++;
      } catch (e) {
        toast.error(`Upload "${f.name}" failed: ${e.response?.data?.detail || e.message}`);
      }
    }
    if (added) toast.success(`Added ${added} reference${added === 1 ? "" : "s"}`);
    setUploadingRef(false);
  };

  const deleteRef = async (refId) => {
    try {
      await axios.delete(`${API}/recipes/image/refs/${refId}`);
      setRefs(prev => prev.filter(r => r.ref_id !== refId));
      setSelectedRefIds(prev => { const n = new Set(prev); n.delete(refId); return n; });
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const toggleRefSelected = (refId) => {
    setSelectedRefIds(prev => {
      const n = new Set(prev);
      if (n.has(refId)) n.delete(refId); else n.add(refId);
      return n;
    });
  };

  const downloadImage = (url, filename) => {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "image_studio.png";
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // ─── Render ────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#0b0f17] text-white pb-24" data-testid="image-studio-page">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-[#0b0f17]/90 backdrop-blur-md border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-3">
          <button onClick={() => navigate("/recipes")}
            className="p-2 rounded-lg hover:bg-white/5 transition-colors text-white/60 hover:text-white"
            data-testid="image-studio-back-btn">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500/30 to-sky-500/30 flex items-center justify-center">
            <Sparkles className="w-4 h-4 text-emerald-300" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-semibold truncate">Image Studio</h1>
            <p className="text-[11px] text-white/40">
              Standalone gpt-image-2 · BYO keys · ~$0.016–0.063 / image
            </p>
          </div>
          <span className="text-[10px] text-emerald-400 flex items-center gap-1.5 bg-emerald-500/10 px-2 py-1 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> OpenAI ✓
          </span>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* ─── LEFT: Form ────────────────────────────── */}
        <div className="lg:col-span-7 space-y-4">
          {/* Mode toggle */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setMode("single")}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                mode === "single"
                  ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"
                  : "bg-white/5 text-white/50 border border-white/10 hover:bg-white/10"
              }`}
              data-testid="mode-single-btn"
            >
              Single Prompt
            </button>
            <button
              onClick={() => setMode("batch")}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                mode === "batch"
                  ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"
                  : "bg-white/5 text-white/50 border border-white/10 hover:bg-white/10"
              }`}
              data-testid="mode-batch-btn"
            >
              Batch · paste breakdown
            </button>
          </div>

          {/* Prompt input */}
          {mode === "single" ? (
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
              <label className="text-[11px] font-medium text-white/60 uppercase tracking-wider mb-2 block">
                Prompt
              </label>
              <textarea
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                placeholder="A wide morning shot of petrol scooters weaving through Indian metro traffic, exhaust haze, floating rupee counters ticking up over each rider…"
                rows={6}
                className="w-full bg-black/30 border border-white/10 rounded-lg p-3 text-[13px] text-white/90 leading-relaxed outline-none focus:border-emerald-500/40 resize-y"
                data-testid="prompt-textarea"
              />
              <div className="text-[10px] text-white/30 mt-1.5 text-right">{prompt.length} chars</div>
            </div>
          ) : (
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
              <label className="text-[11px] font-medium text-white/60 uppercase tracking-wider mb-2 block">
                Breakdown — paste your shot list
              </label>
              <p className="text-[10px] text-white/40 mb-2">
                Lines starting with <code className="text-emerald-300">[SH01]</code>,{" "}
                <code className="text-emerald-300">SH-02:</code>, etc. auto-split into separate prompts.
                Without markers, the whole text fires as one prompt.
              </p>
              <textarea
                value={breakdownText}
                onChange={e => setBreakdownText(e.target.value)}
                placeholder="[SH01] Wide of bustling Indian metro road, hard morning sun, exhaust haze.
[SH02] Tight low side-tracking on rider, visor flipped up, first warm light.
[SH03] Product packshot — Ampere scooter on neutral plinth, brand-card lighting."
                rows={10}
                className="w-full bg-black/30 border border-white/10 rounded-lg p-3 text-[12px] text-white/90 leading-relaxed font-mono outline-none focus:border-emerald-500/40 resize-y"
                data-testid="breakdown-textarea"
              />
              <div className="text-[10px] text-white/30 mt-1.5 flex justify-between">
                <span>{breakdownText.length} chars</span>
                <span>{promptCount} prompt{promptCount === 1 ? "" : "s"} detected</span>
              </div>
            </div>
          )}

          {/* Engine / aspect / resolution / count */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="Engine">
              <select value={engine} onChange={e => setEngine(e.target.value)}
                className="w-full bg-black/30 border border-white/10 rounded-md px-2 py-1.5 text-xs text-white/90 outline-none focus:border-emerald-500/40"
                data-testid="engine-select">
                <option value="gpt-image-2">gpt-image-2</option>
                <option value="gemini" disabled>Gemini (Pass 2)</option>
                <option value="auto">auto</option>
              </select>
            </Field>
            <Field label="Aspect">
              <select value={aspectRatio} onChange={e => setAspectRatio(e.target.value)}
                className="w-full bg-black/30 border border-white/10 rounded-md px-2 py-1.5 text-xs text-white/90 outline-none focus:border-emerald-500/40"
                data-testid="aspect-select">
                {ASPECTS.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            </Field>
            <Field label="Resolution">
              <select value={resolution} onChange={e => setResolution(e.target.value)}
                className="w-full bg-black/30 border border-white/10 rounded-md px-2 py-1.5 text-xs text-white/90 outline-none focus:border-emerald-500/40"
                data-testid="res-select">
                <option value="1k">1K · medium</option>
                <option value="2k">2K · high</option>
              </select>
            </Field>
            <Field label="Count / prompt">
              <input type="number" min={1} max={4} value={count}
                onChange={e => setCount(parseInt(e.target.value, 10) || 1)}
                className="w-full bg-black/30 border border-white/10 rounded-md px-2 py-1.5 text-xs text-white/90 outline-none focus:border-emerald-500/40"
                data-testid="count-input" />
            </Field>
          </div>

          {/* References panel */}
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Paperclip className="w-4 h-4 text-white/60" />
                <span className="text-[11px] font-medium text-white/60 uppercase tracking-wider">
                  Reference Images <span className="text-emerald-400">({selectedRefIds.size} selected)</span>
                </span>
              </div>
              <div className="flex items-center gap-2">
                <select value={refRoleOnUpload} onChange={e => setRefRoleOnUpload(e.target.value)}
                  className="bg-black/30 border border-white/10 rounded px-2 py-1 text-[10px] text-white/80"
                  data-testid="ref-role-select">
                  {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
                <input ref={refFileInputRef} type="file" multiple accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={e => { handleRefFiles(e.target.files, refRoleOnUpload); e.target.value = ""; }}
                  data-testid="ref-file-input" />
                <button onClick={() => refFileInputRef.current?.click()}
                  disabled={uploadingRef}
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 text-[11px] font-medium disabled:opacity-50"
                  data-testid="ref-upload-btn">
                  {uploadingRef ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
                  Upload
                </button>
              </div>
            </div>
            <p className="text-[10px] text-white/30 mb-3 leading-relaxed">
              💡 <span className="text-white/50">Consistency tip:</span> Upload one approved <code className="text-emerald-300">character</code> ref
              and the same subject carries across every prompt in the batch — no Asset Bible needed.
            </p>
            {refs.length === 0 ? (
              <div className="text-[11px] text-white/40 text-center py-6 border border-dashed border-white/10 rounded-lg">
                No references yet — upload to anchor character / costume / style across runs.
              </div>
            ) : (
              <div className="grid grid-cols-6 gap-2">
                {refs.map(r => {
                  const sel = selectedRefIds.has(r.ref_id);
                  return (
                    <div key={r.ref_id} className="relative group" data-testid={`ref-tile-${r.ref_id}`}>
                      <button onClick={() => toggleRefSelected(r.ref_id)}
                        className={`relative w-full aspect-square rounded-lg overflow-hidden border-2 transition-all ${
                          sel ? "border-emerald-400 ring-2 ring-emerald-400/30" : "border-white/10 hover:border-white/30"
                        }`}
                        title={r.label || r.filename}>
                        <img src={resolveUrl(r.url)} alt={r.label || r.filename}
                          className="w-full h-full object-cover" />
                        <span className="absolute bottom-0 left-0 right-0 bg-black/70 text-[8px] py-0.5 uppercase tracking-wider text-center"
                          style={{ color: sel ? "#34D399" : "#94A3B8" }}>
                          {r.role_hint || "ref"}
                        </span>
                      </button>
                      <button onClick={() => deleteRef(r.ref_id)}
                        className="absolute top-1 right-1 p-0.5 rounded-full bg-black/70 text-white/80 opacity-0 group-hover:opacity-100 hover:bg-rose-500"
                        title="Delete reference">
                        <X className="w-2.5 h-2.5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Advanced */}
          <div className="rounded-xl border border-white/10 bg-white/[0.02]">
            <button onClick={() => setAdvancedOpen(o => !o)}
              className="w-full px-4 py-3 flex items-center justify-between text-[11px] font-medium uppercase tracking-wider text-white/60 hover:text-white"
              data-testid="advanced-toggle">
              <span className="flex items-center gap-2">
                <Settings2 className="w-3.5 h-3.5" /> Advanced
              </span>
              <span className="text-white/30">{advancedOpen ? "−" : "+"}</span>
            </button>
            {advancedOpen && (
              <div className="px-4 pb-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                <Field label="Negative prompt (product/CGI)">
                  <input value={negativePrompt} onChange={e => setNegativePrompt(e.target.value)}
                    placeholder="warped reflections, stock-photo backdrop"
                    className="w-full bg-black/30 border border-white/10 rounded-md px-2 py-1.5 text-xs text-white/90 outline-none focus:border-emerald-500/40"
                    data-testid="negative-prompt-input" />
                </Field>
                <Field label="Seed (reproducibility)">
                  <input type="number" value={seed} onChange={e => setSeed(e.target.value)}
                    placeholder="auto"
                    className="w-full bg-black/30 border border-white/10 rounded-md px-2 py-1.5 text-xs text-white/90 outline-none focus:border-emerald-500/40"
                    data-testid="seed-input" />
                </Field>
                <Field label="Audit tier">
                  <div className="text-[10px] text-white/40 py-1.5">Phase 2 — VLM audit + correction (coming next).</div>
                </Field>
              </div>
            )}
          </div>

          {/* Estimate + Generate */}
          <div className="sticky bottom-4 z-10 bg-[#0b0f17]/90 backdrop-blur-md border border-white/10 rounded-xl p-3 flex items-center gap-3">
            <button onClick={fetchEstimate} disabled={estimating || generating}
              className="px-3 py-2 rounded-lg text-xs font-medium bg-white/5 hover:bg-white/10 text-white/80 border border-white/10 flex items-center gap-1.5 disabled:opacity-50"
              data-testid="estimate-btn">
              {estimating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <DollarSign className="w-3.5 h-3.5" />}
              Estimate
            </button>
            {estimate && (
              <div className="flex-1 text-[11px] text-white/70">
                <span className="text-emerald-400 font-mono font-bold">${estimate.total_usd}</span>
                <span className="text-white/40"> = ${estimate.per_image_usd}/img × {estimate.n_images} ({estimate.engine}, {estimate.resolution})</span>
              </div>
            )}
            {!estimate && <div className="flex-1" />}
            <button onClick={runGeneration}
              disabled={generating || promptCount === 0}
              className="px-4 py-2 rounded-lg text-sm font-semibold bg-gradient-to-r from-emerald-500 to-sky-500 hover:from-emerald-400 hover:to-sky-400 text-white flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed active:scale-95 transition-all"
              data-testid="generate-btn">
              {generating
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Generating…</>
                : <><Sparkles className="w-4 h-4" /> Generate {promptCount > 1 ? `(${promptCount})` : ""}</>}
            </button>
          </div>
        </div>

        {/* ─── RIGHT: Results + history ────────────────── */}
        <div className="lg:col-span-5 space-y-4">
          {currentRun && (
            <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04] p-4" data-testid="current-run-card">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="text-sm font-semibold text-emerald-300">Latest Run</h3>
                  <p className="text-[10px] text-white/40">
                    {currentRun.run_id?.slice(0, 8)}… · {currentRun.status} · ${currentRun.total_cost_usd}
                  </p>
                </div>
                <span className="text-[10px] text-white/40">{currentRun.count} image{currentRun.count === 1 ? "" : "s"}</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {(currentRun.results || []).flatMap(r =>
                  (r.image_urls || []).map((url, i) => ({
                    url, id: `${r.id}-${i}`, prompt: r.prompt, shotId: r.id, error: r.error,
                  })),
                ).map(item => (
                  <div key={item.id} className="relative aspect-video rounded-lg overflow-hidden bg-black/40 border border-white/10 group cursor-zoom-in"
                    onClick={() => setLightboxUrl(item.url)}
                    data-testid={`result-tile-${item.shotId}`}>
                    <img src={item.url} alt={item.shotId} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-end justify-between p-2 opacity-0 group-hover:opacity-100">
                      <span className="text-[10px] font-mono text-white bg-black/60 px-1.5 py-0.5 rounded">{item.shotId}</span>
                      <div className="flex gap-1">
                        <button onClick={(e) => { e.stopPropagation(); setLightboxUrl(item.url); }}
                          className="p-1.5 rounded-md bg-black/70 hover:bg-black/90 text-white"
                          title="Expand">
                          <Maximize2 className="w-3 h-3" />
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); downloadImage(item.url, `${item.shotId}.png`); }}
                          className="p-1.5 rounded-md bg-black/70 hover:bg-black/90 text-white"
                          title="Download">
                          <Download className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                {(currentRun.results || []).filter(r => r.error).map(r => (
                  <div key={r.id} className="aspect-video rounded-lg bg-rose-500/10 border border-rose-500/30 p-2 flex flex-col gap-1"
                    data-testid={`result-failed-${r.id}`}>
                    <span className="text-[10px] font-mono text-rose-300">{r.id} · failed</span>
                    <span className="text-[10px] text-white/50 leading-tight">{r.error}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
            <h3 className="text-[11px] font-medium text-white/60 uppercase tracking-wider mb-3 flex items-center gap-2">
              <ImageIcon className="w-3.5 h-3.5" /> Run History
            </h3>
            {runHistory.length === 0 ? (
              <div className="text-[11px] text-white/40 text-center py-4">No runs yet.</div>
            ) : (
              <div className="space-y-2">
                {runHistory.map(r => (
                  <button key={r.run_id} onClick={async () => {
                    try {
                      const { data } = await axios.get(`${API}/recipes/image/runs/${r.run_id}`);
                      setCurrentRun(data);
                    } catch (e) { toast.error("Failed to load run"); }
                  }}
                    className="w-full text-left px-3 py-2 rounded-lg bg-white/[0.02] hover:bg-white/[0.05] border border-white/5 transition-colors"
                    data-testid={`history-row-${r.run_id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[11px] font-mono text-white/70 truncate">
                        {r.run_id.slice(0, 8)}… · {r.engine}
                      </span>
                      <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${
                        r.status === "done" ? "bg-emerald-500/15 text-emerald-300" :
                        r.status === "partial" ? "bg-amber-500/15 text-amber-300" :
                        r.status === "failed" ? "bg-rose-500/15 text-rose-300" :
                        "bg-white/10 text-white/50"
                      }`}>{r.status}</span>
                    </div>
                    <div className="text-[10px] text-white/40 mt-0.5">
                      ${r.total_cost_usd || 0} · {new Date(r.created_at).toLocaleString()}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxUrl && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-6 cursor-zoom-out"
          onClick={() => setLightboxUrl(null)}
          data-testid="lightbox">
          <button onClick={(e) => { e.stopPropagation(); setLightboxUrl(null); }}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white">
            <X className="w-5 h-5" />
          </button>
          <img src={lightboxUrl} alt="Expanded" className="max-w-full max-h-[90vh] rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] font-medium text-white/50 uppercase tracking-wider mb-1 block">{label}</label>
      {children}
    </div>
  );
}

function resolveUrl(url) {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${BASE_URL}${url}`;
}
